import { motion, AnimatePresence } from "framer-motion";
import SidebarItem from "./SidebarItem";
import { sidebarMenuItems } from "./sidebarConfig";
import type { SidebarMenuItem, SidebarChild } from "./sidebarConfig";
import { useSidebar } from "@src/presentation/contexts/SidebarContext";
import { ScreenSize } from "@src/presentation/hooks/useScreenSize";
import { ThemeSwitch } from "@src/presentation/components/ThemeSwitch";
import { usePermission } from "@src/presentation/hooks/usePermission";
import { useMemo } from "react";

const Sidebar = () => {
  const { expanded, screenType, isOpen, setIsOpen } = useSidebar();
  const isMobile = screenType === ScreenSize.Mobile;
  const { hasPermission, hasAllPermissions, hasAnyPermission, isSuperAdmin } = usePermission();

  /**
   * Check if user has permission to view a menu item
   */
  const hasMenuPermission = (item: SidebarMenuItem | SidebarChild): boolean => {
    // Super admins see everything
    if (isSuperAdmin()) {
      return true;
    }

    // If no permissions specified, show by default
    if (!item.permission && !item.requiredPermissions && !item.anyPermissions) {
      return true;
    }

    // Check single permission
    if (item.permission) {
      return hasPermission(item.permission);
    }

    // Check all required permissions
    if (item.requiredPermissions && item.requiredPermissions.length > 0) {
      return hasAllPermissions(item.requiredPermissions);
    }

    // Check any of the permissions
    if (item.anyPermissions && item.anyPermissions.length > 0) {
      return hasAnyPermission(item.anyPermissions);
    }

    return true;
  };

  /**
   * Filter menu items and their children based on permissions
   */
  const filteredMenuItems = useMemo(() => {
    return sidebarMenuItems
      .filter((item) => item.showInNav && hasMenuPermission(item))
      .map((item) => {
        // If item has children, filter them too
        if (item.children) {
          const filteredChildren = item.children.filter((child) => hasMenuPermission(child));

          // Only show parent if it has at least one visible child
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...item,
            children: filteredChildren,
          };
        }

        return item;
      })
      .filter(Boolean) as SidebarMenuItem[];
  }, [hasPermission, hasAllPermissions, hasAnyPermission, isSuperAdmin]);

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-lg z-[400]"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar / Drawer */}
      <motion.div
        className={`${isMobile ? "fixed left-0 top-14 bottom-0 z-[500]" : "relative top-14"
          } bg-secondary border-r border-border overflow-hidden flex flex-col h-[calc(100vh-3.5rem)]`}
        animate={{
          width: isMobile
            ? isOpen
              ? "240px"
              : "0px"
            : expanded
              ? screenType === "laptop"
                ? "200px"
                : "240px" // Slightly smaller on laptop
              : "72px",
          transition: { duration: 0.3, ease: "easeInOut" },
        }}
        initial={false}
      >
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-4 px-2">
            {filteredMenuItems.map((item) => (
              <SidebarItem
                key={item.label}
                icon={item.icon}
                label={item.label}
                route={item.route}
                isDisabled={item.isDisabled}
                children={item.children}
              />
            ))}
          </ul>
        </nav>

        <div className="border-t border-border p-4">
          <ThemeSwitch isIconOnly={(isMobile || screenType === ScreenSize.Tablet) && !expanded} />
        </div>
      </motion.div>
    </>
  );
};

export default Sidebar;
